import { useState } from "react";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

dayjs.locale("pt-br");
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

// Mapa fixo de cores para evitar problema de purge CSS com classes dinâmicas
const COLOR_MAP = {
    indigo: {
        selected: "bg-indigo-600 text-white",
        rangeEdge: "bg-indigo-600 text-white",
        inRange: "bg-indigo-100 text-indigo-800",
        highlighted: "bg-amber-300 text-amber-900",
        hover: "hover:bg-indigo-100 hover:text-indigo-700",
    },
    violet: {
        selected: "bg-violet-600 text-white",
        rangeEdge: "bg-violet-600 text-white",
        inRange: "bg-violet-100 text-violet-800",
        highlighted: "bg-amber-300 text-amber-900",
        hover: "hover:bg-violet-100 hover:text-violet-700",
    },
    sky: {
        selected: "bg-sky-600 text-white",
        rangeEdge: "bg-sky-600 text-white",
        inRange: "bg-sky-100 text-sky-800",
        highlighted: "bg-amber-300 text-amber-900",
        hover: "hover:bg-sky-100 hover:text-sky-700",
    },
    emerald: {
        selected: "bg-emerald-600 text-white",
        rangeEdge: "bg-emerald-600 text-white",
        inRange: "bg-emerald-100 text-emerald-800",
        highlighted: "bg-amber-300 text-amber-900",
        hover: "hover:bg-emerald-100 hover:text-emerald-700",
    },
    rose: {
        selected: "bg-rose-600 text-white",
        rangeEdge: "bg-rose-600 text-white",
        inRange: "bg-rose-100 text-rose-800",
        highlighted: "bg-amber-300 text-amber-900",
        hover: "hover:bg-rose-100 hover:text-rose-700",
    },
};

const getColors = (color) => COLOR_MAP[color] ?? COLOR_MAP.indigo;

function CalendarInline({
    mode = "single",
    value = null,
    onChange,
    rangeValue = { start: null, end: null },
    onRangeChange,
    color = "indigo",
    highlightDates = [],
    disablePast = false,
    onMonthChange,
    cardClassName = "",
}) {
    const initialDate = mode === "range"
        ? (rangeValue?.start ? dayjs(rangeValue.start) : dayjs())
        : (value ? dayjs(value) : dayjs());

    const [currentMonth, setCurrentMonth] = useState(initialDate);
    const [hoverDate, setHoverDate] = useState(null);

    const colors = getColors(color);
    const today = dayjs().startOf("day");

    const startOfMonth = currentMonth.startOf("month");
    const startDay = startOfMonth.day();
    const daysInMonth = currentMonth.daysInMonth();

    const days = [
        ...Array(startDay).fill(null),
        ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
    ];

    const changeMonth = (newMonth) => {
        setCurrentMonth(newMonth);
        onMonthChange?.(newMonth.format("YYYY-MM"));
    };

    const getDate = (day) => currentMonth.date(day).startOf("day");

    // --- Handlers ---

    const handleSelect = (day) => {
        const selected = getDate(day);

        if (mode === "single") {
            const formatted = selected.format("YYYY-MM-DD");
            onChange?.(selected.isSame(dayjs(value), "day") ? null : formatted);
            return;
        }

        // Modo range
        const formatted = selected.format("YYYY-MM-DD");
        const { start, end } = rangeValue ?? {};

        // Nenhuma data selecionada ainda → define start
        if (!start) {
            onRangeChange?.({ start: formatted, end: null });
            return;
        }

        const startDjs = dayjs(start);

        // Já tem start e end definidos → reinicia com nova data como start
        if (start && end) {
            onRangeChange?.({ start: formatted, end: null });
            return;
        }

        // Só tem start, sem end ainda:
        if (selected.isSame(startDjs, "day")) {
            // 2º clique na mesma data → define end igual ao start (range de 1 dia)
            onRangeChange?.({ start, end: formatted });
        } else if (selected.isBefore(startDjs)) {
            // Clicou numa data anterior → inverte a ordem
            onRangeChange?.({ start: formatted, end: start });
        } else {
            // Clicou numa data posterior → define end normalmente
            onRangeChange?.({ start, end: formatted });
        }
    };

    // --- Estado visual de cada dia ---

    const getDayState = (day) => {
        const date = getDate(day);

        if (mode === "single") {
            return {
                isSelected: value ? date.isSame(dayjs(value), "day") : false,
                isStart: false,
                isEnd: false,
                inRange: false,
            };
        }

        const { start, end } = rangeValue ?? {};
        const startDjs = start ? dayjs(start).startOf("day") : null;
        const endDjs = end ? dayjs(end).startOf("day") : null;
        const previewEnd = !end && hoverDate ? dayjs(hoverDate).startOf("day") : null;
        const effectiveEnd = endDjs ?? previewEnd;

        const isStart = startDjs ? date.isSame(startDjs, "day") : false;
        const isEnd = effectiveEnd ? date.isSame(effectiveEnd, "day") : false;

        let inRange = false;
        if (startDjs && effectiveEnd && !isStart && !isEnd) {
            const [lo, hi] = startDjs.isBefore(effectiveEnd)
                ? [startDjs, effectiveEnd]
                : [effectiveEnd, startDjs];
            inRange = date.isAfter(lo) && date.isBefore(hi);
        }

        return { isSelected: false, isStart, isEnd, inRange };
    };

    const getDayClass = (day) => {
        const date = getDate(day);
        const isToday = date.isSame(today, "day");
        const isHighlighted = highlightDates.includes(date.format("YYYY-MM-DD"));
        const { isSelected, isStart, isEnd, inRange } = getDayState(day);

        const base = "w-9 h-9 text-sm font-normal flex items-center justify-center transition-colors focus:outline-none";

        if (isSelected) {
            return `${base} rounded-full ${colors.selected}`;
        }

        if (isStart && isEnd) {
            return `${base} rounded-full ${colors.rangeEdge}`;
        }

        if (isStart) {
            return `${base} rounded-l-full ${colors.rangeEdge}`;
        }

        if (isEnd) {
            return `${base} rounded-r-full ${colors.rangeEdge}`;
        }

        if (inRange) {
            return `${base} rounded-none ${colors.inRange}`;
        }

        if (isHighlighted) {
            return `${base} rounded-full ${colors.highlighted}`;
        }

        if (isToday) {
            return `${base} rounded-full ring-1 ring-current font-medium ${colors.hover}`;
        }

        return `${base} rounded-full ${colors.hover}`;
    };

    return (
        <div className={cardClassName}>
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
                <button
                    onClick={() => changeMonth(currentMonth.subtract(1, "month"))}
                    className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-muted text-muted-foreground transition-colors"
                    aria-label="Mês anterior"
                >
                    ‹
                </button>

                <span className="font-display font-medium text-base capitalize select-none text-foreground">
                    {currentMonth.format("MMMM YYYY")}
                </span>

                <button
                    onClick={() => changeMonth(currentMonth.add(1, "month"))}
                    className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-muted text-muted-foreground transition-colors"
                    aria-label="Próximo mês"
                >
                    ›
                </button>
            </div>

            {/* Dias da semana */}
            <div className="grid grid-cols-7 text-center text-xs text-muted-foreground mb-1 select-none">
                {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((d) => (
                    <div key={d}>{d}</div>
                ))}
            </div>

            {/* Dias */}
            <div className="grid grid-cols-7 gap-1 text-center">
                {days.map((day, index) => {
                    if (!day) return <div key={index} className="w-9 h-9" />;

                    const isDisabled = disablePast && getDate(day).isBefore(today);

                    return (
                        <button
                            key={index}
                            disabled={isDisabled}
                            onClick={() => handleSelect(day)}
                            onMouseEnter={() => {
                                if (mode === "range") setHoverDate(getDate(day).format("YYYY-MM-DD"));
                            }}
                            onMouseLeave={() => {
                                if (mode === "range") setHoverDate(null);
                            }}
                            className={`${getDayClass(day)} ${isDisabled ? "opacity-30 cursor-not-allowed" : "cursor-pointer"}`}
                        >
                            {day}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export default CalendarInline;
